import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse, Org, User } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { SocketAdminService } from './socketadmin.service';
import { UsertokenService } from './usertoken.service';

export const logout = () => {
  if (window.location.origin.startsWith("http://localhost") || window.location.origin.startsWith("https://dev-ki-toolbox.reflactportal.com")) {
    window.location.href = 'https://portal-dev.reflact.info/';
  } else {
    window.location.href = 'https://reflactportal.com/';
  }
};

type SystemConf = "kitoolbox" | "devkitoolbox" | "stagingkitoolbox"
@Injectable({
  providedIn: 'root'
})
export class LoginService {
  public loggedInUser: null | User<string, string> = null;
  public myOrgs: Org[] = [];
  public systemConf: SystemConf | "loading" = "loading";

  constructor(private httpClient: HttpClient, public socketAdmin: SocketAdminService, private usertokenService: UsertokenService) {
    void this.loadSystemConf();
  }
  public getInitials() {
    if (!this.loggedInUser?.name) return '';
    const parts = this.loggedInUser.name.trim().split(' ');
    if (parts.length === 1) {
      return parts.at(0)?.charAt(0).toUpperCase() ?? '';
    }
    return (parts.at(0)?.charAt(0).toUpperCase() + "" + parts.at(-1)?.charAt(0).toUpperCase());

  }


  public async loadSystemConf() {
    this.systemConf = (await firstValueFrom(this.httpClient.get<DefaultResponse & { appIdentifier: SystemConf }>('/api/conf'))).appIdentifier;
  }

  public hasBotAdmin() {
    return this.loggedInUser?.admin === true;
  }

  public hasRagAdmin() {
    return this.loggedInUser?.email.endsWith("@reflact.com");
  }

  public hasSUPERAdmin() {
    // @ts-expect-error this field can be set
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.loggedInUser?.superadmin;
  }

  public isAdminRoute() {
    return window.location.href.includes("bot-admin") || window.location.href.includes("portallogin");;
  }

  public async loginByPortalToken(token: string) {
    const headers: Record<string, string | string[]> = {};
    headers['portaltoken'] = token;
    const result = await firstValueFrom(this.httpClient.post<{ token: string }>("/api/toolbox/auth/loginbyportaltoken/", {}, { headers }));
    this.usertokenService.setUserTokenAfterAuth(result.token);
  }
  public async loginAsGuest(persist: boolean = true) {
    const result = await firstValueFrom(this.httpClient.post<{ token: string }>("/api/toolbox/auth/loginasguest/", {}));
    this.usertokenService.setUserTokenAfterAuth(result.token, persist);
  }

  // persist=false: Token bleibt bis zur Datenschutz-Bestaetigung nur im Speicher (siehe UsertokenService.persistToken)
  public async loadMyUser(persist: boolean = true) {
    try {
      if (this.usertokenService.getUserToken() == null) {
        await this.loginAsGuest(persist);
      }
      const loaded = await firstValueFrom(this.httpClient.get<{ user: User<string, string>, usertoken: string }>('/api/toolbox/auth/myuser'));
      this.loggedInUser = {
        ...loaded.user
      };
      this.usertokenService.setUserTokenAfterAuth(loaded.usertoken, persist); // Damit der token nicht abläuft
      await this.loadMyOrgs();

      if (this.loggedInUser.admin && this.isAdminRoute()) this.socketAdmin.connect();
    } catch (e) {
      console.warn("user not logged in for loadMyUser", e);
      this.loggedInUser = null;
    }
    return this.loggedInUser;
  }

  public async loadMyOrgs() {
    const orgs = await firstValueFrom(this.httpClient.get<Org[]>('/api/toolbox/auth/myorgs'));
    this.myOrgs = orgs.toSorted((a, b) => a.name.localeCompare(b.name));
  }


  public async logout(logoutFromServer: boolean) {
    if (logoutFromServer) {
      await firstValueFrom(this.httpClient.post("/api/toolbox/auth/logout", {}));

      this.loggedInUser = null;
      this.usertokenService.logoutUserByUnsetToken();
    }
    if (window.location.origin.startsWith("http://localhost") || window.location.origin.startsWith("https://dev-ki-toolbox.reflactportal.com")) {
      window.location.href = 'https://portal-dev.reflact.info/';
    } else {
      window.location.href = 'https://reflactportal.com/';
    }
  }

  public async setAdminPosition(position: string) {
    const io = await this.socketAdmin.getIo();
    io.emit("hereIsTheAdmin", { position });
  }

  public getMyOrgSorted(): string[] | undefined {
    return this.loggedInUser?.orgShorts.toSorted((a, b) => a.localeCompare(b));
  }
}
